import { useMemo } from 'react';

import { LocationsGeoFilterInput, OfferFragment, useOfferQuery } from '@ocx/graphql';
import { useMembership } from '@ocx/data-membership';
import { mapFragmentsToSortedOffersList, sanitizeAvailableAtLocations } from './offer.dto';
import { Offer, SanitizedLocation } from './offer.types';

type UseOfferParams = {
  offerId: string;
  limitsIncluded?: boolean;
  vouchersIncluded?: boolean;
  locationsGeoFilter?: LocationsGeoFilterInput;
};

export type OfferWithSanitizedLocations = Omit<Offer, 'availableAtLocations'> & {
  availableAtLocations: SanitizedLocation[];
};

export const useOffer = ({ offerId, limitsIncluded, vouchersIncluded, locationsGeoFilter }: UseOfferParams) => {
  const { membership } = useMembership();
  const { data, loading } = useOfferQuery({
    variables: {
      offerId,
      offerLimitsIncluded: limitsIncluded,
      offerVouchersIncluded: vouchersIncluded,
      locationsGeoFilter: locationsGeoFilter || undefined,
      availableAtLocationsIncluded: true,
      constraintsIncluded: true,
    },
    fetchPolicy: 'cache-and-network',
  });

  const offer: OfferWithSanitizedLocations | null = useMemo(() => {
    if (!data?.customer?.membership?.offers?.edges) {
      return null;
    }

    const fragments = data.customer.membership.offers.edges
      .filter((edge): edge is { node: OfferFragment } => !!edge?.node)
      .map(({ node }) => node);
    const offers = mapFragmentsToSortedOffersList({
      offers: fragments,
      membershipPoints: membership.points,
    });

    if (!offers.length) {
      return null;
    }

    return {
      ...offers[0],
      availableAtLocations: sanitizeAvailableAtLocations(offers[0].availableAtLocations),
    };
  }, [data?.customer?.membership?.offers?.edges, membership.points]);

  return { offer, loading };
};
